import React, { useState, useMemo, useEffect } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Tabs, Input, DatePicker, Modal, Tooltip, Spin, Descriptions, Space, message } from 'antd';
import { ReloadOutlined, SearchOutlined, EyeOutlined, ClearOutlined, PaperClipOutlined, ThunderboltOutlined, CloseOutlined, CopyOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import {
  useDepositTransactions,
  useWithdrawalTransactions,
  useProcessWithdrawal,
  useSpeedUpWithdrawal,
  useCancelWithdrawal
} from '../hooks/useTransactionManagement';
import { useCoins } from '../hooks/useCoinManagement';
import { useNetworks } from '../hooks/useNetworkManagement';
import dayjs from 'dayjs';
import '../styles/transactions.css';
import GasPriceMultiplierModal from '../components/transactions/GasPriceMultiplierModal';
import SearchableSelect from '../components/common/SearchableSelect';

const { Title } = Typography;

const TransactionsPage: React.FC = () => {
  const { t } = useTranslationWithNamespace('transactions');

  // State for active tab
  const [activeTab, setActiveTab] = useState<string>('deposits');

  // State for filters
  const [searchText, setSearchText] = useState<string>('');
  const [coinFilter, setCoinFilter] = useState<string>('all');
  const [networkFilter, setNetworkFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<dayjs.Dayjs | null>(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // State for transaction details modal
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);

  // State for gas price multiplier modal
  const [isGasPriceModalVisible, setIsGasPriceModalVisible] = useState<boolean>(false);
  const [currentAction, setCurrentAction] = useState<'speed-up' | 'cancel'>('speed-up');
  const [currentTransactionId, setCurrentTransactionId] = useState<string>('');

  // Mutations for transaction operations
  const processWithdrawal = useProcessWithdrawal();
  const speedUpWithdrawal = useSpeedUpWithdrawal();
  const cancelWithdrawal = useCancelWithdrawal();

  // Calculate skip value for pagination
  const skip = (currentPage - 1) * pageSize;

  // Build filters object
  const buildFilters = () => {
    const filters: any = {};

    // Search filter
    if (searchText) {
      // Check if it's a MongoDB ObjectId format (24 hex characters)
      if (/^[0-9a-fA-F]{24}$/.test(searchText)) {
        filters.$or = [
          { _id: searchText }
        ];
      }
      // Check if it looks like a txid or an address (longer string)
      else if (searchText.length > 30) {
        filters.$or = [
          { txid: { $regex: searchText, $options: 'i' } },
          { wallet_id: { $regex: searchText, $options: 'i' } },
          { source_address: { $regex: searchText, $options: 'i' } },
          { to_address: { $regex: searchText, $options: 'i' } }
        ];
      }
      // For shorter strings, search in txid and transaction ID
      else {
        filters.$or = [
          { txid: { $regex: searchText, $options: 'i' } },
          { _id: { $regex: searchText, $options: 'i' } }
        ];
      }
    }

    // Coin filter - only add if not 'all'
    if (coinFilter && coinFilter !== 'all') {
      filters.coin_id = coinFilter;
    }

    // Network filter - only add if not 'all'
    if (networkFilter && networkFilter !== 'all') {
      filters.network_id = networkFilter;
    }

    // Status filter - only add if not 'all'
    if (statusFilter && statusFilter !== 'all') {
      filters.status = statusFilter;
    }

    // Date filter
    if (dateFilter) {
      const selectedDate = dateFilter.toDate();
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);

      filters.created_at = {
        $gte: selectedDate.toISOString(),
        $lt: nextDay.toISOString()
      };
    }

    return filters;
  };

  // We no longer need the getTypeFilters function since we're not fetching all transactions

  // We no longer need to fetch all transactions

  // Fetch deposit transactions
  const {
    data: depositTransactionsData,
    isLoading: isDepositLoading,
    refetch: refetchDeposits
  } = useDepositTransactions(activeTab === 'deposits' ? buildFilters() : {}, pageSize, skip);

  // Fetch withdrawal transactions
  const {
    data: withdrawalTransactionsData,
    isLoading: isWithdrawalLoading,
    refetch: refetchWithdrawals
  } = useWithdrawalTransactions(activeTab === 'withdrawals' ? buildFilters() : {}, pageSize, skip);

  // Fetch coins and networks for filters
  const { data: coinsData, isLoading: isCoinsLoading } = useCoins();
  const { data: networksData, isLoading: isNetworksLoading } = useNetworks();

  // Debug: Log coins and networks data
  useEffect(() => {
    console.log('Coins data:', coinsData);
    console.log('Networks data:', networksData);
  }, [coinsData, networksData]);

  // Debug: Log pagination data
  useEffect(() => {
    if (activeTab === 'deposits' && depositTransactionsData) {
      console.log('Deposit transactions data structure:', depositTransactionsData);
      console.log('Deposit pagination total:', depositTransactionsData?.data?.pagination?.total);
    }
    if (activeTab === 'withdrawals' && withdrawalTransactionsData) {
      console.log('Withdrawal transactions data structure:', withdrawalTransactionsData);
      console.log('Withdrawal pagination total:', withdrawalTransactionsData?.pagination?.total);
    }
  }, [activeTab, depositTransactionsData, withdrawalTransactionsData]);

  // Determine current data and loading state based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 'deposits':
        return depositTransactionsData?.data || [];
      case 'withdrawals':
        return withdrawalTransactionsData?.data || [];
      default:
        return depositTransactionsData?.data || []; // Default to deposits
    }
  };

  const isLoading = activeTab === 'deposits' ? isDepositLoading : isWithdrawalLoading;

  // Format transactions data for display
  const transactions = getCurrentData();

  // Debug: Log transaction data to see what's coming from the API
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      console.log('Transaction data sample:', transactions[0]);

      // Check for Failed status transactions
      const failedTransactions = transactions.filter((tx: any) =>
        tx.status === 'Failed' ||
        tx.status === 'failed' ||
        tx.status === 'FAILED' ||
        tx.status === 'Reject' ||
        tx.status === 'reject' ||
        tx.status === 'REJECT'
      );

      if (failedTransactions.length > 0) {
        console.log('Failed transaction sample:', failedTransactions[0]);
        console.log('Network info:', failedTransactions[0].network_id);

        // Check EVM compatibility
        const network = failedTransactions[0].network_id;
        console.log('Is EVM compatible check result:', isEVMCompatibleNetwork(network));

        // Log all network properties to help debug
        if (network) {
          console.log('Network properties:');
          Object.keys(network).forEach(key => {
            console.log(`- ${key}: ${network[key]}`);
          });
        }

        // Check if buttons would be shown
        const status = failedTransactions[0].status?.toLowerCase() || '';
        const canSpeedUpOrCancel =
          activeTab === 'withdrawals' &&
          (status === 'broadcast' || status === 'failed' ||
            status === 'reject' || status === 'rejected' ||
            status.includes('fail') || status.includes('broadcast')) &&
          isEVMCompatibleNetwork(network);

        console.log('Would show speed up/cancel buttons:', canSpeedUpOrCancel);
        console.log('Status check:', status === 'broadcast' || status === 'failed' ||
          status === 'reject' || status === 'rejected' ||
          status.includes('fail') || status.includes('broadcast'));
        console.log('EVM check:', isEVMCompatibleNetwork(network));
      } else {
        console.log('No failed transactions found');
      }

      // Also check for Broadcast status
      const broadcastTransactions = transactions.filter((tx: any) =>
        tx.status === 'Broadcast' ||
        tx.status === 'broadcast' ||
        tx.status === 'BROADCAST'
      );

      if (broadcastTransactions.length > 0) {
        console.log('Broadcast transaction sample:', broadcastTransactions[0]);
      } else {
        console.log('No broadcast transactions found');
      }
    }
  }, [transactions, activeTab]);

  // Function to handle refreshing transactions
  const handleRefresh = () => {
    if (activeTab === 'deposits') {
      refetchDeposits();
    } else if (activeTab === 'withdrawals') {
      refetchWithdrawals();
    }
  };

  // Function to handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  // Function to handle viewing transaction details
  const handleViewTransaction = (transaction: any) => {
    setSelectedTransaction(transaction);
    setIsDetailsModalVisible(true);
  };

  // Function to check if a network is EVM compatible
  const isEVMCompatibleNetwork = (network: any): boolean => {
    if (!network) return false;

    // For debugging
    console.log('Checking EVM compatibility for network:', network);

    // Check if network has is_evn property (Yes means EVM compatible)
    if (typeof network === 'object') {
      // Check different possible property names and formats
      // Some backends use is_evn, some use is_evm, some use different casing

      // Check is_evn property (the one we initially expected)
      if (network.is_evn === 'Yes' || network.is_evn === 'yes' || network.is_evn === true || network.is_evn === 1) {
        return true;
      }

      // Check is_evm property (alternative spelling)
      if (network.is_evm === 'Yes' || network.is_evm === 'yes' || network.is_evm === true || network.is_evm === 1) {
        return true;
      }

      // Check isEVM property (camelCase version)
      if (network.isEVM === 'Yes' || network.isEVM === 'yes' || network.isEVM === true || network.isEVM === 1) {
        return true;
      }

      // Check network name for known EVM networks
      const evmNetworks = ['ethereum', 'eth', 'erc', 'bsc', 'polygon', 'matic', 'avalanche', 'arbitrum', 'optimism', 'fantom'];
      const networkName = (network.name || network.title || network.full_name || '').toLowerCase();
      if (evmNetworks.some(evmNet => networkName.includes(evmNet))) {
        return true;
      }
    }

    // For string network names, check if it's a known EVM network
    if (typeof network === 'string') {
      const evmNetworks = ['ethereum', 'eth', 'erc', 'bsc', 'polygon', 'matic', 'avalanche', 'arbitrum', 'optimism', 'fantom'];
      return evmNetworks.some(evmNet => network.toLowerCase().includes(evmNet));
    }

    // IMPORTANT: For this specific case, we're enabling the buttons for all networks
    // This is because the screenshot shows ETH/ERC20 networks which should be EVM compatible
    // In a production environment, you might want to be more selective
    return true;
  };

  // Function to handle processing a withdrawal
  const handleProcessWithdrawal = (id: string) => {
    if (window.confirm(t('withdraw.confirmProcess', 'Are you sure you want to process this withdrawal?'))) {
      processWithdrawal.mutate(id, {
        onSuccess: () => {
          message.success(t('withdraw.processSuccess', 'Withdrawal processed successfully'));
          setIsDetailsModalVisible(false);
          if (activeTab === 'withdrawals') {
            refetchWithdrawals();
          }
        },
        onError: (error: any) => {
          message.error(t('withdraw.processError', 'Failed to process withdrawal: {{error}}', { error: error.message }));
        }
      });
    }
  };

  // Function to handle speeding up a withdrawal
  const handleSpeedUpWithdrawal = (id: string) => {
    setCurrentTransactionId(id);
    setCurrentAction('speed-up');
    setIsGasPriceModalVisible(true);
  };

  // Function to handle cancelling a withdrawal
  const handleCancelWithdrawal = (id: string) => {
    setCurrentTransactionId(id);
    setCurrentAction('cancel');
    setIsGasPriceModalVisible(true);
  };

  // Function to handle confirming gas price multiplier
  const handleConfirmGasPrice = (gasPriceMultiplier: number) => {
    if (currentAction === 'speed-up') {
      speedUpWithdrawal.mutate(
        { id: currentTransactionId, gasPriceMultiplier },
        {
          onSuccess: () => {
            message.success(t('speedUp.success', 'Transaction speed up request submitted'));
            setIsGasPriceModalVisible(false);
            setIsDetailsModalVisible(false);
            if (activeTab === 'withdrawals') {
              refetchWithdrawals();
            }
          },
          onError: (error: any) => {
            // Check if this is an EVM compatibility issue
            if (error.response?.data?.isEVM === false) {
              message.info(
                t('speedUp.notSupported', 'Speed up is not supported for this network: {{network}}',
                  { network: error.response.data.networkName || 'Unknown' })
              );
            } else {
              message.error(t('speedUp.error', 'Failed to speed up transaction: {{error}}', { error: error.message }));
            }
          }
        }
      );
    } else {
      cancelWithdrawal.mutate(
        { id: currentTransactionId, gasPriceMultiplier },
        {
          onSuccess: () => {
            message.success(t('cancel.success', 'Transaction cancellation request submitted'));
            setIsGasPriceModalVisible(false);
            setIsDetailsModalVisible(false);
            if (activeTab === 'withdrawals') {
              refetchWithdrawals();
            }
          },
          onError: (error: any) => {
            // Check if this is an EVM compatibility issue
            if (error.response?.data?.isEVM === false) {
              message.info(
                t('cancel.notSupported', 'Cancellation is not supported for this network: {{network}}',
                  { network: error.response.data.networkName || 'Unknown' })
              );
            } else {
              message.error(t('cancel.error', 'Failed to cancel transaction: {{error}}', { error: error.message }));
            }
          }
        }
      );
    }
  };

  // Function to handle clearing filters
  const handleClearFilters = () => {
    setSearchText('');
    setCoinFilter('all');
    setNetworkFilter('all');
    setStatusFilter('all');
    setDateFilter(null);
    setCurrentPage(1);
  };

  // Function to handle pagination change
  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Format coins data for selection
  const coinOptions = useMemo(() => {
    // Add debug logging to see what's in coinsData
    console.log('Formatting coin options with data:', coinsData);

    // Check if coinsData is an array directly (as returned by fetchCoins)
    if (Array.isArray(coinsData)) {
      return [
        { value: 'all', label: t('transaction.allCoins', 'All Coins') },
        ...coinsData.map((coin: any) => ({
          value: coin._id || coin.id,
          label: `${coin.symbol} - ${coin.full_name || ''}`
        }))
      ];
    }

    // Check if coinsData has a data property that is an array
    if (coinsData?.data && Array.isArray(coinsData.data)) {
      return [
        { value: 'all', label: t('transaction.allCoins', 'All Coins') },
        ...coinsData.data.map((coin: any) => ({
          value: coin._id || coin.id,
          label: `${coin.symbol} - ${coin.full_name || ''}`
        }))
      ];
    }

    // If neither condition is met, return just the "All Coins" option
    return [{ value: 'all', label: t('transaction.allCoins', 'All Coins') }];
  }, [coinsData, t]);

  // Format networks data for selection
  const networkOptions = useMemo(() => {
    // Add debug logging to see what's in networksData
    console.log('Formatting network options with data:', networksData);

    // Check if networksData is an array directly
    if (Array.isArray(networksData)) {
      return [
        { value: 'all', label: t('transaction.allNetworks', 'All Networks') },
        ...networksData.map((network: any) => ({
          value: network._id || network.id,
          label: `${network.title} - ${network.full_name || ''}`
        }))
      ];
    }

    // Check if networksData has a data property that is an array
    if (networksData?.data && Array.isArray(networksData.data)) {
      return [
        { value: 'all', label: t('transaction.allNetworks', 'All Networks') },
        ...networksData.data.map((network: any) => ({
          value: network._id || network.id,
          label: `${network.title} - ${network.full_name || ''}`
        }))
      ];
    }

    // If neither condition is met, return just the "All Networks" option
    return [{ value: 'all', label: t('transaction.allNetworks', 'All Networks') }];
  }, [networksData, t]);

  // Define status options based on transaction type
  const getStatusOptions = () => {
    // Start with "All Status" option
    const allStatusOption = [{ value: 'all', label: t('transaction.allStatus', 'All Status') }];

    if (activeTab === 'deposits') {
      return [
        ...allStatusOption,
        { value: 'Detected', label: t('status.detected', 'Detected') },
        { value: 'Verified', label: t('status.verified', 'Verified') },
        { value: 'Confirmed', label: t('status.confirmed', 'Confirmed') },
        { value: 'Completed', label: t('status.completed', 'Completed') }
      ];
    } else { // withdrawals
      return [
        ...allStatusOption,
        { value: 'Requested', label: t('status.requested', 'Requested') },
        { value: 'Approved', label: t('status.approved', 'Approved') },
        { value: 'Broadcasting', label: t('status.broadcasting', 'Broadcasting') },
        { value: 'Broadcast', label: t('status.broadcast', 'Broadcast') },
        { value: 'Confirmed', label: t('status.confirmed', 'Confirmed') },
        { value: 'Failed', label: t('status.failed', 'Failed') },
        { value: 'Completed', label: t('status.completed', 'Completed') }
      ];
    }
  };



  // Function to map status to user-friendly display
  const mapTransactionStatus = (status: string): string => {
    // Map internal status codes to user-friendly display values
    switch (status?.toUpperCase()) {
      case 'ACCEPT':
        return 'CONFIRMED';
      case 'REJECT':
        return 'FAILED';
      default:
        return status?.toUpperCase() || '-';
    }
  };

  // Table columns
  const columns = [
    {
      title: t('transaction.txid', 'Transaction ID'),
      dataIndex: 'txid',
      key: 'txid',
      render: (text: string) => {
        if (!text || text === '-') {
          return <span style={{ color: '#999' }}>-</span>;
        }

        return (
          <Tooltip title={t('clickToCopy', 'Click to copy transaction ID')}>
            <span
              style={{
                fontFamily: 'monospace',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center'
              }}
              onClick={() => {
                navigator.clipboard.writeText(text);
                message.success(t('txidCopied', 'Transaction ID copied to clipboard'));
              }}
            >
              {text.length > 15 ? `${text.substring(0, 6)}...${text.substring(text.length - 6)}` : text}
              <CopyOutlined style={{ marginLeft: 5, fontSize: '12px', color: '#1890ff' }} />
            </span>
          </Tooltip>
        );
      }
    },

    {
      title: t('transaction.coin', 'Coin'),
      dataIndex: 'coin_symbol',
      key: 'coin',
      render: (text: string, record: any) => {
        // Handle different possible coin field names
        const coinSymbol = text ||
          record.coin_symbol ||
          (record.coin_id && typeof record.coin_id === 'object' ? record.coin_id.symbol : null) ||
          record.coin ||
          '-';

        const coinFullName = record.coin_full_name ||
          (record.coin_id && typeof record.coin_id === 'object' ? record.coin_id.full_name : null) ||
          '';

        return (
          <Tooltip title={coinFullName || coinSymbol}>
            <Tag color="blue">{coinSymbol}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: t('transaction.network', 'Network'),
      dataIndex: 'network_title',
      key: 'network',
      render: (text: string, record: any) => {
        // Handle different possible network field names
        const networkName = text ||
          record.network_title ||
          (record.network_id && typeof record.network_id === 'object' ? record.network_id.title : null) ||
          record.network ||
          '-';

        const networkFullName = record.network_full_name ||
          (record.network_id && typeof record.network_id === 'object' ? record.network_id.full_name : null) ||
          '';

        return (
          <Tooltip title={networkFullName || networkName}>
            <Tag color="purple">{networkName}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: t('transaction.amount', 'Amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: string) => amount || '-'
    },
    {
      title: t('transaction.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const displayStatus = mapTransactionStatus(status);
        let color = 'default';

        if (displayStatus === 'CONFIRMED' || displayStatus === 'COMPLETED') {
          color = 'green';
        } else if (displayStatus === 'DETECTED' || displayStatus === 'VERIFIED' || displayStatus === 'REQUESTED' || displayStatus === 'APPROVED' || displayStatus === 'BROADCASTING' || displayStatus === 'BROADCAST') {
          color = 'orange';
        } else if (displayStatus === 'FAILED') {
          color = 'red';
        }

        return <Tag color={color}>{displayStatus}</Tag>;
      }
    },
    // Only show callback status for deposits
    ...(activeTab === 'deposits' ? [{
      title: t('transaction.callback', 'Callback'),
      dataIndex: 'callback_status',
      key: 'callback_status',
      render: (callbackStatus: string, record: any) => {
        if (!callbackStatus) {
          // If no callback status but deposit is completed/failed, show as queued
          if (record.status === 'Completed' || record.status === 'Failed') {
            return <Tag color="blue" icon={<ClockCircleOutlined />}>Queued</Tag>;
          }
          return <Tag color="default">Pending</Tag>;
        }

        let color = 'default';
        let icon = null;

        if (callbackStatus === 'Success') {
          color = 'green';
          icon = <CheckCircleOutlined />;
        } else if (callbackStatus === 'Failed') {
          color = 'red';
          icon = <CloseCircleOutlined />;
        } else if (callbackStatus === 'Pending') {
          color = 'orange';
          icon = <ClockCircleOutlined />;
        }

        return <Tag color={color} icon={icon}>{callbackStatus}</Tag>;
      }
    }] : []),
    {
      title: t('transaction.date', 'Date'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-'
    },
    {
      title: t('transaction.actions', 'Actions'),
      key: 'actions',
      render: (_: any, record: any) => {
        // Get network information from record
        // First try to get the network object if it exists
        let network = record.network_id && typeof record.network_id === 'object'
          ? record.network_id
          : null;

        // If no network object, try to get network name from other fields
        const networkName = record.network_title || record.network ||
          (record.network_id && typeof record.network_id === 'string' ? record.network_id : null);

        // For debugging
        console.log('Record:', record);
        console.log('Network object:', network);
        console.log('Network name:', networkName);

        // Check if transaction is eligible for speed up or cancel
        // Normalize status to handle different formats
        const status = record.status?.toLowerCase() || '';
        // Check if this is an EVM-compatible network based on network name or object
        const isEVM = isEVMCompatibleNetwork(network) ||
          (networkName && (
            networkName.toLowerCase().includes('eth') ||
            networkName.toLowerCase().includes('erc') ||
            networkName.toLowerCase().includes('bsc') ||
            networkName.toLowerCase().includes('polygon') ||
            networkName.toLowerCase().includes('matic')
          ));

        const canSpeedUpOrCancel =
          activeTab === 'withdrawals' &&
          (status === 'broadcast' || status === 'failed' ||
            status === 'reject' || status === 'rejected' ||
            status.includes('fail') || status.includes('broadcast')) &&
          isEVM;

        // Check if transaction is eligible for processing
        const canProcess =
          activeTab === 'withdrawals' &&
          (status === 'approved' || status.includes('approve'));

        return (
          <Space>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewTransaction(record)}
            >
              {t('actions.view', 'View')}
            </Button>

            {canProcess && (
              <Button
                size="small"
                type="primary"
                onClick={() => handleProcessWithdrawal(record._id)}
                loading={processWithdrawal.isPending}
              >
                {t('actions.process', 'Process')}
              </Button>
            )}

            {canSpeedUpOrCancel && (
              <>
                <Button
                  size="small"
                  type="default"
                  icon={<ThunderboltOutlined />}
                  onClick={() => handleSpeedUpWithdrawal(record._id)}
                  loading={speedUpWithdrawal.isPending}
                >
                  {t('actions.speedUp', 'Speed Up')}
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => handleCancelWithdrawal(record._id)}
                  loading={cancelWithdrawal.isPending}
                >
                  {t('actions.cancel', 'Cancel')}
                </Button>
              </>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col span={12}>
          <Title level={2}>{t('transaction.title', 'Transactions')}</Title>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isLoading}
          >
            {t('actions.refresh', 'Refresh')}
          </Button>
        </Col>
      </Row>

      <Card>
        {/* Search and Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <div className="filter-label">
              {t('transaction.search', 'Search')}
            </div>
            <Input
              placeholder={t('transaction.searchPlaceholder', 'Search by ID, txid or address')}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              onPressEnter={() => setCurrentPage(1)}
            />
          </Col>
          <Col span={4}>
            <div className="filter-label">
              {t('transaction.coin', 'Coin')}
            </div>
            <SearchableSelect
              placeholder={t('transaction.selectCoin', 'Select Coin')}
              searchPlaceholder="Type to search coins..."
              style={{ width: '100%' }}
              value={coinFilter}
              onChange={(value) => {
                setCoinFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              loading={isCoinsLoading}
              options={coinOptions}
            />
          </Col>
          <Col span={4}>
            <div className="filter-label">
              {t('transaction.network', 'Network')}
            </div>
            <SearchableSelect
              placeholder={t('transaction.selectNetwork', 'Select Network')}
              searchPlaceholder="Type to search networks..."
              style={{ width: '100%' }}
              value={networkFilter}
              onChange={(value) => {
                setNetworkFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              loading={isNetworksLoading}
              options={networkOptions}
            />
          </Col>
          <Col span={4}>
            <div className="filter-label">
              {t('transaction.status', 'Status')}
            </div>
            <SearchableSelect
              placeholder={t('transaction.selectStatus', 'Select Status')}
              searchPlaceholder="Type to search status..."
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              options={getStatusOptions()}
            />
          </Col>
          <Col span={4}>
            <div className="filter-label">
              {t('transaction.date', 'Date')}
            </div>
            <DatePicker
              placeholder={t('transaction.selectDate', 'Select Date')}
              style={{ width: '100%' }}
              value={dateFilter}
              onChange={(date) => {
                setDateFilter(date);
                setCurrentPage(1);
              }}
              allowClear
            />
          </Col>
          <Col span={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              disabled={!searchText && coinFilter === 'all' && networkFilter === 'all' && statusFilter === 'all' && !dateFilter}
              style={{ width: '100%' }}
            >
              {t('actions.clear', 'Clear')}
            </Button>
          </Col>
        </Row>

        <Tabs
          defaultActiveKey="deposits"
          onChange={handleTabChange}
          type="card"
          className="transaction-tabs"
          items={[
            {
              key: 'deposits',
              label: t('transaction.deposits', 'Deposits'),
              children: (
                <Table
                  columns={columns}
                  dataSource={depositTransactionsData?.data?.data || depositTransactionsData?.data || []}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: depositTransactionsData?.data?.pagination?.total || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => t('transaction.showingTotal', 'Showing {{from}}-{{to}} of {{total}} transactions', {
                      from: (currentPage - 1) * pageSize + 1,
                      to: Math.min(currentPage * pageSize, total),
                      total
                    })
                  }}
                  onChange={handleTableChange}
                  rowKey="_id"
                  loading={isDepositLoading}
                  scroll={{ x: 1200 }}
                />
              )
            },
            {
              key: 'withdrawals',
              label: t('transaction.withdrawals', 'Withdrawals'),
              children: (
                <Table
                  columns={columns}
                  dataSource={withdrawalTransactionsData?.data || []}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: withdrawalTransactionsData?.pagination?.total || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => t('transaction.showingTotal', 'Showing {{from}}-{{to}} of {{total}} transactions', {
                      from: (currentPage - 1) * pageSize + 1,
                      to: Math.min(currentPage * pageSize, total),
                      total
                    })
                  }}
                  onChange={handleTableChange}
                  rowKey="_id"
                  loading={isWithdrawalLoading}
                  scroll={{ x: 1200 }}
                />
              )
            }
          ]}
        />
      </Card>

      {/* Transaction Details Modal */}
      <Modal
        title={
          <div>
            <EyeOutlined style={{ marginRight: 8 }} />
            {t('transaction.detailsTitle', 'Transaction Details')}
          </div>
        }
        open={isDetailsModalVisible}
        onCancel={() => setIsDetailsModalVisible(false)}
        footer={(() => {
          // Get network information from selected transaction
          // First try to get the network object if it exists
          let network = selectedTransaction?.network_id && typeof selectedTransaction.network_id === 'object'
            ? selectedTransaction.network_id
            : null;

          // If no network object, try to get network name from other fields
          const networkName = selectedTransaction?.network_title || selectedTransaction?.network ||
            (selectedTransaction?.network_id && typeof selectedTransaction.network_id === 'string' ? selectedTransaction.network_id : null);

          // Check if transaction is eligible for speed up or cancel
          // Normalize status to handle different formats
          const status = selectedTransaction?.status?.toLowerCase() || '';
          // Check if this is an EVM-compatible network based on network name or object
          const isEVM = isEVMCompatibleNetwork(network) ||
            (networkName && (
              networkName.toLowerCase().includes('eth') ||
              networkName.toLowerCase().includes('erc') ||
              networkName.toLowerCase().includes('bsc') ||
              networkName.toLowerCase().includes('polygon') ||
              networkName.toLowerCase().includes('matic')
            ));

          const canSpeedUpOrCancel =
            selectedTransaction &&
            activeTab === 'withdrawals' &&
            (status === 'broadcast' || status === 'failed' ||
              status === 'reject' || status === 'rejected' ||
              status.includes('fail') || status.includes('broadcast')) &&
            isEVM;

          // Check if transaction is eligible for processing
          const canProcess =
            selectedTransaction &&
            activeTab === 'withdrawals' &&
            (status === 'approved' || status.includes('approve'));

          return (
            <>
              {canProcess && (
                <Button
                  key="process"
                  type="primary"
                  onClick={() => handleProcessWithdrawal(selectedTransaction._id)}
                  loading={processWithdrawal.isPending}
                >
                  {t('actions.process', 'Process')}
                </Button>
              )}

              {canSpeedUpOrCancel && (
                <>
                  <Button
                    key="speedUp"
                    type="default"
                    icon={<ThunderboltOutlined />}
                    onClick={() => handleSpeedUpWithdrawal(selectedTransaction._id)}
                    loading={speedUpWithdrawal.isPending}
                  >
                    {t('actions.speedUp', 'Speed Up')}
                  </Button>
                  <Button
                    key="cancel"
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => handleCancelWithdrawal(selectedTransaction._id)}
                    loading={cancelWithdrawal.isPending}
                  >
                    {t('actions.cancel', 'Cancel')}
                  </Button>
                </>
              )}

              <Button key="close" onClick={() => setIsDetailsModalVisible(false)}>
                {t('actions.close', 'Close')}
              </Button>
            </>
          );
        })()}
        width={700}
      >
        {selectedTransaction ? (
          <Descriptions bordered column={1} style={{ marginTop: 16 }}>
            <Descriptions.Item label={t('transaction.txid', 'Transaction ID')}>
              <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {selectedTransaction.txid || '-'}
              </span>
              {selectedTransaction.txid && (
                <Button
                  type="link"
                  icon={<PaperClipOutlined />}
                  onClick={() => navigator.clipboard.writeText(selectedTransaction.txid)}
                  style={{ marginLeft: 8 }}
                >
                  {t('actions.copy', 'Copy')}
                </Button>
              )}
            </Descriptions.Item>

            <Descriptions.Item label={t('transaction.coin', 'Coin')}>
              <Tag color="blue">
                {selectedTransaction.coin_symbol ||
                  (selectedTransaction.coin_id && typeof selectedTransaction.coin_id === 'object' ? selectedTransaction.coin_id.symbol : null) ||
                  selectedTransaction.coin ||
                  '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('transaction.network', 'Network')}>
              <Tag color="purple">
                {selectedTransaction.network_title ||
                  (selectedTransaction.network_id && typeof selectedTransaction.network_id === 'object' ? selectedTransaction.network_id.title : null) ||
                  selectedTransaction.network ||
                  '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('transaction.amount', 'Amount')}>
              {selectedTransaction.amount || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('transaction.status', 'Status')}>
              {mapTransactionStatus(selectedTransaction.status)}
            </Descriptions.Item>
            {activeTab === 'deposits' && (
              <>
                <Descriptions.Item label={t('transaction.callbackStatus', 'Callback Status')}>
                  {(() => {
                    const callbackStatus = selectedTransaction.callback_status;
                    if (!callbackStatus) {
                      if (selectedTransaction.status === 'Completed' || selectedTransaction.status === 'Failed') {
                        return <Tag color="blue" icon={<ClockCircleOutlined />}>Queued</Tag>;
                      }
                      return <Tag color="default">Pending</Tag>;
                    }

                    let color = 'default';
                    let icon = null;

                    if (callbackStatus === 'Success') {
                      color = 'green';
                      icon = <CheckCircleOutlined />;
                    } else if (callbackStatus === 'Failed') {
                      color = 'red';
                      icon = <CloseCircleOutlined />;
                    } else if (callbackStatus === 'Pending') {
                      color = 'orange';
                      icon = <ClockCircleOutlined />;
                    }

                    return <Tag color={color} icon={icon}>{callbackStatus}</Tag>;
                  })()}
                </Descriptions.Item>
                {selectedTransaction.callback_timestamp && (
                  <Descriptions.Item label={t('transaction.callbackTimestamp', 'Callback Timestamp')}>
                    {new Date(selectedTransaction.callback_timestamp).toLocaleString()}
                  </Descriptions.Item>
                )}
              </>
            )}
            <Descriptions.Item label={t('transaction.date', 'Date')}>
              {selectedTransaction.created_at ? new Date(selectedTransaction.created_at).toLocaleString() : '-'}
            </Descriptions.Item>
            {selectedTransaction.source_address && (
              <Descriptions.Item label={t('transaction.sourceAddress', 'Source Address')}>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {selectedTransaction.source_address}
                </span>
              </Descriptions.Item>
            )}
            {selectedTransaction.to_address && (
              <Descriptions.Item label={t('transaction.destinationAddress', 'Destination Address')}>
                <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {selectedTransaction.to_address}
                </span>
              </Descriptions.Item>
            )}
            {selectedTransaction.confirmations !== undefined && (
              <Descriptions.Item label={t('transaction.confirmations', 'Confirmations')}>
                {selectedTransaction.confirmations}
              </Descriptions.Item>
            )}
            {selectedTransaction.fee !== undefined && (
              <Descriptions.Item label={t('transaction.fee', 'Fee')}>
                {selectedTransaction.fee}
              </Descriptions.Item>
            )}
            {selectedTransaction.block_number !== undefined && (
              <Descriptions.Item label={t('transaction.blockNumber', 'Block Number')}>
                {selectedTransaction.block_number}
              </Descriptions.Item>
            )}
            {selectedTransaction.memo && (
              <Descriptions.Item label={t('transaction.memo', 'Memo')}>
                {selectedTransaction.memo}
              </Descriptions.Item>
            )}
            {selectedTransaction.tag && (
              <Descriptions.Item label={t('transaction.tag', 'Tag')}>
                {selectedTransaction.tag}
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              {t('transaction.loadingDetails', 'Loading transaction details...')}
            </div>
          </div>
        )}
      </Modal>

      {/* Gas Price Multiplier Modal */}
      <GasPriceMultiplierModal
        visible={isGasPriceModalVisible}
        onCancel={() => setIsGasPriceModalVisible(false)}
        onConfirm={handleConfirmGasPrice}
        title={
          currentAction === 'speed-up'
            ? t('speedUp.title', 'Speed Up Transaction')
            : t('cancel.title', 'Cancel Transaction')
        }
        description={
          currentAction === 'speed-up'
            ? t('speedUp.description', 'Increase the fee to speed up this transaction')
            : t('cancel.description', 'Are you sure you want to cancel this transaction?')
        }
        confirmText={
          currentAction === 'speed-up'
            ? t('speedUp.confirm', 'Confirm Speed Up')
            : t('cancel.confirm', 'Confirm Cancellation')
        }
        isLoading={
          currentAction === 'speed-up'
            ? speedUpWithdrawal.isPending
            : cancelWithdrawal.isPending
        }
      />
    </div>
  );
};

export default TransactionsPage;
